import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { ArrowLeft, MapPin, Upload, Loader2, X, Check, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { connectMeetApi, EventType, CreateEventData, EventDateTime, AUDIENCE_OPTIONS } from '../services/connectMeetApi';
import { toast } from 'react-hot-toast';

// ─── Constants ─────────────────────────────────────────────────────────────

const DUTCH_PROVINCES = [
  'Groningen', 'Friesland', 'Drenthe', 'Overijssel', 'Flevoland',
  'Gelderland', 'Utrecht', 'Noord-Holland', 'Zuid-Holland',
  'Zeeland', 'Noord-Brabant', 'Limburg',
];

interface EventTypeOption {
  type: EventType;
  label: string;
  emoji: string;
  description: string;
}

const EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  { type: 'borrel', label: 'Borrel / Vrijmibo', emoji: '🍺', description: 'Informeel samenzijn na werktijd' },
  { type: 'lunch', label: 'Lunch', emoji: '🍽️', description: 'Samen lunchen en netwerken' },
  { type: 'wandeling', label: 'Wandeling', emoji: '🚶', description: 'Wandelen en praten in de buitenlucht' },
  { type: 'grab_a_coffee', label: 'Grab a coffee', emoji: '☕', description: 'Koffie en een goed gesprek' },
  { type: 'samen_werken', label: 'Samen werken', emoji: '💻', description: 'Gezamenlijk werken op locatie' },
  { type: 'anders', label: 'Anders', emoji: '✨', description: 'Iets anders, omschrijf zelf' },
];

// ─── Gallery images per event type ─────────────────────────────────────────

type GalleryImage = { id: string; url: string; alt: string };

const GALLERY: Record<EventType | '_default', GalleryImage[]> = {
  borrel: [
    { id: 'b1', url: 'https://images.unsplash.com/photo-1574096079513-d8259312b785?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Borrel – proosten' },
    { id: 'b2', url: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Craft beer' },
    { id: 'b3', url: 'https://images.unsplash.com/photo-1567529327-3e50f3c74d52?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Biertjes op rij' },
    { id: 'b4', url: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Wijnglazen' },
    { id: 'b5', url: 'https://images.unsplash.com/photo-1575444758702-4a6b9222336e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bar interieur' },
    { id: 'b6', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Feest cheers' },
    { id: 'b7', url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Borrel party' },
    { id: 'b8', url: 'https://images.unsplash.com/photo-1559526324-593bc073d938?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Cocktails kleurrijk' },
    { id: 'b9', url: 'https://images.unsplash.com/photo-1547782793-e7b5ba0f9cb2?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Diner met drank' },
    { id: 'b10', url: 'https://images.unsplash.com/photo-1571115177098-24ec42ed204d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Wijn rosé' },
    { id: 'b11', url: 'https://images.unsplash.com/photo-1612837017391-4b6b7b0e2b3a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Terras borrel' },
    { id: 'b12', url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Snacks & drinks' },
    { id: 'b13', url: 'https://images.unsplash.com/photo-1543353071-087092ec393a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Gezellig tafelen' },
    { id: 'b14', url: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Gezelschap bijeenkomst' },
    { id: 'b15', url: 'https://images.unsplash.com/photo-1529543544282-ea669407fca3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Happy hour' },
  ],
  lunch: [
    { id: 'l1', url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Restaurant elegant' },
    { id: 'l2', url: 'https://images.unsplash.com/photo-1553621042-f6e147245754?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Lunch borden' },
    { id: 'l3', url: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Zakenlunch' },
    { id: 'l4', url: 'https://images.unsplash.com/photo-1530062845289-9109b2c9c868?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Mensen lunchen' },
    { id: 'l5', url: 'https://images.unsplash.com/photo-1565895405138-6c3a1555da6a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Spread gerechten' },
    { id: 'l6', url: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Café lunch' },
    { id: 'l7', url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Restaurant sfeer' },
    { id: 'l8', url: 'https://images.unsplash.com/photo-1512058564366-18510be2db19?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Sushi lunch' },
    { id: 'l9', url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Kleurrijk eten' },
    { id: 'l10', url: 'https://images.unsplash.com/photo-1481931098730-318b6f776db0?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Lunch spread' },
    { id: 'l11', url: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Salade lunch' },
    { id: 'l12', url: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Pannenkoeken lunch' },
    { id: 'l13', url: 'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Lunch tafel' },
    { id: 'l14', url: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Mediterraan' },
    { id: 'l15', url: 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bowl lunch' },
  ],
  wandeling: [
    { id: 'w1', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Pad in natuur' },
    { id: 'w2', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bospad' },
    { id: 'w3', url: 'https://images.unsplash.com/photo-1501555088652-021faa106b9b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Wandelen bos' },
    { id: 'w4', url: 'https://images.unsplash.com/photo-1528543606781-2f6e8b0df82d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Stadswandeling' },
    { id: 'w5', url: 'https://images.unsplash.com/photo-1543799634-e1d49dc1f27a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Park wandeling' },
    { id: 'w6', url: 'https://images.unsplash.com/photo-1474511320723-9a56873867b5?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Buitenlucht groen' },
    { id: 'w7', url: 'https://images.unsplash.com/photo-1527402762793-dab5e4eb5a2b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bankje in park' },
    { id: 'w8', url: 'https://images.unsplash.com/photo-1444210971048-6130cf0c46cf?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Natuurpad' },
    { id: 'w9', url: 'https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Wandelen samen' },
    { id: 'w10', url: 'https://images.unsplash.com/photo-1564839824-20e1f1e0da10?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Wandelen langs water' },
    { id: 'w11', url: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Herfstbos' },
    { id: 'w12', url: 'https://images.unsplash.com/photo-1443926886625-c87ecf63b77b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Natuur lente' },
    { id: 'w13', url: 'https://images.unsplash.com/photo-1453872302360-eed3c5f8ff66?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Strand wandeling' },
    { id: 'w14', url: 'https://images.unsplash.com/photo-1465310477141-6fb93167a273?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bloemenveld' },
    { id: 'w15', url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Duinen wandeling' },
  ],
  grab_a_coffee: [
    { id: 'c1', url: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Latte art' },
    { id: 'c2', url: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie kop' },
    { id: 'c3', url: 'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffiebonen' },
    { id: 'c4', url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Mensen in café' },
    { id: 'c5', url: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Café interieur' },
    { id: 'c6', url: 'https://images.unsplash.com/photo-1463797221720-6b07e6426c24?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Espresso' },
    { id: 'c7', url: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Moka pot' },
    { id: 'c8', url: 'https://images.unsplash.com/photo-1459755486867-b55449bb39ff?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie met vrienden' },
    { id: 'c9', url: 'https://images.unsplash.com/photo-1521302080334-4bebac2763a6?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffieshop sfeer' },
    { id: 'c10', url: 'https://images.unsplash.com/photo-1522992319-0365e5f11656?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Twee koffies gesprek' },
    { id: 'c11', url: 'https://images.unsplash.com/photo-1559496417-e7f25cb247f3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie tafel' },
    { id: 'c12', url: 'https://images.unsplash.com/photo-1498804103079-a6351b050096?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Flat white' },
    { id: 'c13', url: 'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Cappuccino' },
    { id: 'c14', url: 'https://images.unsplash.com/photo-1517701604599-bb29b565090c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie raam' },
    { id: 'c15', url: 'https://images.unsplash.com/photo-1540190464-e2d81e703f68?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Koffie en cake' },
  ],
  samen_werken: [
    { id: 's1', url: 'https://images.unsplash.com/photo-1521737604082-b0a6f5a5af8f?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Laptop werk open ruimte' },
    { id: 's2', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Teamoverleg' },
    { id: 's3', url: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Vergaderruimte' },
    { id: 's4', url: 'https://images.unsplash.com/photo-1543269664-7eef42226a21?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Coworking ruimte' },
    { id: 's5', url: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Team samenwerking' },
    { id: 's6', url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Business bespreking' },
    { id: 's7', url: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groep werkt samen' },
    { id: 's8', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Open kantoor' },
    { id: 's9', url: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Modern kantoor' },
    { id: 's10', url: 'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Samen coderen' },
    { id: 's11', url: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Whiteboard sessie' },
    { id: 's12', url: 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Presentatie team' },
    { id: 's13', url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Vergadering laptop' },
    { id: 's14', url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Zakelijk overleg' },
    { id: 's15', url: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Team hub' },
  ],
  anders: [
    { id: 'a1', url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Netwerken' },
    { id: 'a2', url: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Bijeenkomst' },
    { id: 'a3', url: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groepsbijeenkomst' },
    { id: 'a4', url: 'https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Evenement' },
    { id: 'a5', url: 'https://images.unsplash.com/photo-1543269664-647b35f78e7b?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Workshop' },
    { id: 'a6', url: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Presentatie' },
    { id: 'a7', url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Online meeting' },
    { id: 'a8', url: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Conference' },
    { id: 'a9', url: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Community event' },
    { id: 'a10', url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Feestelijk samenzijn' },
    { id: 'a11', url: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Zomerevent' },
    { id: 'a12', url: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Outdoor event' },
    { id: 'a13', url: 'https://images.unsplash.com/photo-1484318571209-661cf29a69d3?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Groene bijeenkomst' },
    { id: 'a14', url: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Concert/festival' },
    { id: 'a15', url: 'https://images.unsplash.com/photo-1522158637959-30ab6035b3ad?w=400&h=250&fit=crop&auto=format&q=80', alt: 'Sportief event' },
  ],
  _default: [],
};

// ─── Gallery picker ─────────────────────────────────────────────────────────

const GalleryPicker: React.FC<{
  eventType: EventType | '';
  value: string;
  onChange: (url: string) => void;
}> = ({ eventType, value, onChange }) => {
  const { t } = useTranslation('pages');
  const [showGallery, setShowGallery] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const images: GalleryImage[] = eventType
    ? (GALLERY[eventType] ?? GALLERY._default)
    : [];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await connectMeetApi.uploadImage(file);
      onChange(url);
      setShowGallery(false);
      toast.success(t('connectMeet.photoUploaded'));
    } catch {
      toast.error(t('connectMeet.uploadFailed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={handleFileChange}
        disabled={uploading}
      />

      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-200">
          <img src={value} alt="Event preview" className="w-full h-48 object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 transition-colors"
            title={t('connectMeet.removeImage')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowGallery((v) => !v)}
              disabled={!eventType}
              className="flex-1 flex items-center justify-center gap-2 h-12 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#0B1F3A] hover:text-[#0B1F3A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ImageIcon className="w-4 h-4" />
              {showGallery ? t('connectMeet.hideGallery') : t('connectMeet.chooseFromGallery')}
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 h-12 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#0B1F3A] hover:text-[#0B1F3A] transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {t('connectMeet.uploadOwnPhoto')}
            </button>
          </div>

          {!eventType && (
            <p className="text-xs text-gray-400">{t('connectMeet.chooseTypeFirst')}</p>
          )}

          {showGallery && images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => {
                    onChange(img.url);
                    setShowGallery(false);
                  }}
                  className="relative aspect-video rounded-md overflow-hidden ring-2 ring-transparent hover:ring-[#0B1F3A] focus:outline-none focus:ring-[#0B1F3A] transition-all"
                  title={img.alt}
                >
                  <img
                    src={img.url}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Multi-select checkbox ──────────────────────────────────────────────────

function MultiCheckbox<T extends string>({
  options,
  value,
  onChange,
  columns = 2,
}: {
  options: readonly { value: T; label: string }[];
  value: T[];
  onChange: (next: T[]) => void;
  columns?: number;
}) {
  const toggle = (v: T) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const gridClass = columns === 3
    ? 'grid-cols-2 sm:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div className={`grid ${gridClass} gap-2`}>
      {options.map((opt) => {
        const selected = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-all ${
              selected
                ? 'border-[#0B1F3A] bg-blue-50 text-[#0B1F3A] font-medium'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span
              className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                selected ? 'bg-[#0B1F3A] border-[#0B1F3A]' : 'border-gray-300'
              }`}
            >
              {selected && <Check className="w-3 h-3 text-white" />}
            </span>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Validation ─────────────────────────────────────────────────────────────

interface FormErrors {
  type?: string;
  type_custom?: string;
  event_date?: string;
  time_from?: string;
  location?: string;
  regios?: string;
}

function validate(
  type: EventType | '',
  typeCustom: string,
  eventDate: string,
  timeFrom: string,
  location: string,
  regios: string[],
  t: (key: string) => string
): FormErrors {
  const errors: FormErrors = {};
  if (!type) errors.type = t('connectMeet.errorChooseType');
  if (type === 'anders' && !typeCustom.trim())
    errors.type_custom = t('connectMeet.errorDescribeType');
  if (!eventDate) errors.event_date = t('connectMeet.errorEnterDate');
  if (!timeFrom) errors.time_from = t('connectMeet.errorEnterTime');
  if (!location.trim()) errors.location = t('connectMeet.errorEnterLocation');
  if (regios.length === 0) errors.regios = t('connectMeet.errorChooseRegion');
  return errors;
}

// ─── Extra date-time row ─────────────────────────────────────────────────────

const DateTimeRow: React.FC<{
  index: number;
  value: EventDateTime;
  today: string;
  onChange: (v: EventDateTime) => void;
  onRemove: () => void;
  isFirst: boolean;
}> = ({ index: _index, value, today, onChange, onRemove, isFirst }) => {
  const { t } = useTranslation('pages');
  return (
  <div className="flex flex-col sm:flex-row gap-3 items-start bg-gray-50 rounded-lg p-3 border border-gray-200">
    <div className="flex-1">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {t('connectMeet.date')} {isFirst && <span className="text-red-500">*</span>}
      </label>
      <input
        type="date"
        value={value.date}
        min={today}
        onChange={(e) => onChange({ ...value, date: e.target.value })}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
      />
    </div>
    <div className="w-28">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {t('connectMeet.from')} {isFirst && <span className="text-red-500">*</span>}
      </label>
      <input
        type="time"
        value={value.time_from}
        onChange={(e) => onChange({ ...value, time_from: e.target.value })}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
      />
    </div>
    <div className="w-28">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {t('connectMeet.until')} <span className="text-gray-400 font-normal">({t('connectMeet.optional')})</span>
      </label>
      <input
        type="time"
        value={value.time_to ?? ''}
        onChange={(e) => onChange({ ...value, time_to: e.target.value || undefined })}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
      />
    </div>
    {!isFirst && (
      <div className="pt-5">
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-red-500 transition-colors p-1"
          title={t('connectMeet.removeTimeSlot')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    )}
  </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

/** Datum uit de API (ISO-timestamp of YYYY-MM-DD) → YYYY-MM-DD in lokale tijd voor <input type="date">. */
function naarDatumInput(waarde: string | undefined): string {
  if (!waarde) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(waarde)) return waarde;
  const d = new Date(waarde);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const naarTijdInput = (waarde: string | undefined | null) => (waarde ? String(waarde).slice(0, 5) : '');

export const ConnectMeetCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  // /connect-meet/:id/bewerken → zelfde formulier, opslaan via PUT
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = !!editId;

  const [selectedType, setSelectedType] = useState<EventType | ''>('');
  const [typeCustom, setTypeCustom] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [dateTimes, setDateTimes] = useState<EventDateTime[]>([{ date: '', time_from: '', time_to: undefined }]);
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationPostalCode, setLocationPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [selectedRegios, setSelectedRegios] = useState<string[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<string[]>([]);
  const [maxPersons, setMaxPersons] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const { data: bestaand, isError: laadFout } = useQuery({
    queryKey: ['connect-meet-event', editId],
    queryFn: () => connectMeetApi.getEvent(editId!),
    enabled: isEdit,
  });

  // Formulier één keer vullen met het bestaande event
  const [gevuld, setGevuld] = useState(false);
  useEffect(() => {
    if (!bestaand || gevuld) return;
    setSelectedType(bestaand.type);
    setTypeCustom(bestaand.type_custom ?? '');
    setImageUrl(bestaand.image_url ?? '');
    const extra = typeof bestaand.extra_dates === 'string' ? JSON.parse(bestaand.extra_dates) : bestaand.extra_dates;
    setDateTimes([
      { date: naarDatumInput(bestaand.event_date), time_from: naarTijdInput(bestaand.time_from), time_to: naarTijdInput(bestaand.time_to) || undefined },
      ...((Array.isArray(extra) ? extra : []) as EventDateTime[]).map((dt) => ({
        date: naarDatumInput(dt.date),
        time_from: naarTijdInput(dt.time_from),
        time_to: naarTijdInput(dt.time_to) || undefined,
      })),
    ]);
    // Locatie is bij het aanmaken samengevoegd tot één tekst; die komt terug in het eerste veld.
    setLocationName(bestaand.location ?? '');
    setCity(bestaand.city ?? '');
    setSelectedRegios(bestaand.regios?.length ? bestaand.regios : bestaand.regio ? [bestaand.regio] : []);
    setSelectedAudience(bestaand.audience ?? []);
    setMaxPersons(bestaand.max_persons ? String(bestaand.max_persons) : '');
    setDescription(bestaand.description ?? '');
    setGevuld(true);
  }, [bestaand, gevuld]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateEventData>) => connectMeetApi.updateEvent(editId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connect-meet-event', editId] });
      toast.success(t('connectMeet.eventUpdated', 'Event bijgewerkt'));
      navigate('/connect-meet');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('connectMeet.somethingWentWrong'));
    },
  });
  const opslaanBezig = isEdit ? updateMutation.isPending : false;

  const createMutation = useMutation({
    mutationFn: connectMeetApi.createEvent,
    onSuccess: () => {
      toast.success(t('connectMeet.eventCreated'));
      navigate('/connect-meet');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || t('connectMeet.somethingWentWrong');
      toast.error(msg);
    },
  });

  const primary = dateTimes[0] ?? { date: '', time_from: '', time_to: undefined };

  const revalidate = (
    overrides: Partial<{
      type: EventType | '';
      typeCustom: string;
      eventDate: string;
      timeFrom: string;
      location: string;
      regios: string[];
    }>
  ) => {
    if (!submitted) return;
    setErrors(
      validate(
        overrides.type ?? selectedType,
        overrides.typeCustom ?? typeCustom,
        overrides.eventDate ?? primary.date,
        overrides.timeFrom ?? primary.time_from,
        overrides.location ?? locationName,
        overrides.regios ?? selectedRegios,
        t
      )
    );
  };

  const updateDateTime = (index: number, val: EventDateTime) => {
    const next = [...dateTimes];
    next[index] = val;
    setDateTimes(next);
    if (index === 0) revalidate({ eventDate: val.date, timeFrom: val.time_from });
  };

  const addDateTime = () => {
    setDateTimes([...dateTimes, { date: '', time_from: '', time_to: undefined }]);
  };

  const removeDateTime = (index: number) => {
    setDateTimes(dateTimes.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    const validationErrors = validate(
      selectedType, typeCustom, primary.date, primary.time_from, locationName, selectedRegios, t
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const primaryRegio = selectedRegios[0] ?? '';

    // Combine location parts into a single string
    const combinedLocation = [locationName.trim(), locationAddress.trim(), locationPostalCode.trim()]
      .filter(Boolean)
      .join(', ');

    const extraDates = dateTimes.slice(1).filter(dt => dt.date && dt.time_from);

    const data: CreateEventData = {
      type: selectedType as EventType,
      event_date: primary.date,
      time_from: primary.time_from,
      location: combinedLocation,
      regio: primaryRegio,
      regios: selectedRegios,
    };

    if (selectedType === 'anders' && typeCustom.trim()) data.type_custom = typeCustom.trim();
    if (primary.time_to) data.time_to = primary.time_to;
    if (extraDates.length > 0) data.extra_dates = extraDates;
    if (imageUrl) data.image_url = imageUrl;
    if (description.trim()) data.description = description.trim();
    if (maxPersons && Number(maxPersons) > 0) data.max_persons = Number(maxPersons);
    if (city.trim()) data.city = city.trim();
    if (selectedAudience.length > 0) data.audience = selectedAudience;

    if (isEdit) {
      // PUT werkt alleen velden bij die meegestuurd worden: lege velden expliciet leeg sturen om ze te wissen.
      updateMutation.mutate({
        ...data,
        type_custom: data.type_custom ?? '',
        time_to: data.time_to ?? '',
        extra_dates: extraDates,
        image_url: imageUrl,
        description: description.trim(),
        max_persons: data.max_persons ?? ('' as unknown as number),
        city: city.trim(),
        audience: selectedAudience,
      });
      return;
    }
    createMutation.mutate(data);
  };

  if (isEdit && laadFout) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-sm text-red-600 mb-4">{t('connectMeet.eventNotFound', 'Dit event bestaat niet (meer) of je mag het niet bewerken.')}</p>
        <Link to="/connect-meet" className="text-[#0B1F3A] hover:underline">Connect & Meet</Link>
      </div>
    );
  }
  if (isEdit && !gevuld) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const provinceOptions = DUTCH_PROVINCES.map((p) => ({ value: p, label: p }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link
          to="/connect-meet"
          className="hover:text-[#0B1F3A] flex items-center gap-1 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Connect & Meet
        </Link>
        <span>›</span>
        <span className="text-gray-700">{isEdit ? t('connectMeet.editEvent') : t('connectMeet.newEvent')}</span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEdit ? t('connectMeet.editEvent') : t('connectMeet.createNewEvent')}</h1>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>

        {/* ── Type selector ─────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-1">{t('connectMeet.eventType')}</h2>
          <p className="text-sm text-gray-500 mb-4">{t('connectMeet.chooseEventType')}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {EVENT_TYPE_OPTIONS.map((opt) => {
              const isSelected = selectedType === opt.type;
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => {
                    setSelectedType(opt.type);
                    setImageUrl(''); // reset image when type changes
                    revalidate({ type: opt.type });
                  }}
                  className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-center ${
                    isSelected
                      ? 'border-[#0B1F3A] bg-blue-50 text-[#0B1F3A]'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-2xl">{opt.emoji}</span>
                  <span className="text-sm font-semibold leading-tight">{opt.label}</span>
                  <span className="text-[11px] text-gray-400 leading-snug hidden sm:block">
                    {opt.description}
                  </span>
                </button>
              );
            })}
          </div>

          {errors.type && <p className="mt-2 text-sm text-red-600">{errors.type}</p>}

          {selectedType === 'anders' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('connectMeet.describeEventType')}
              </label>
              <input
                type="text"
                value={typeCustom}
                onChange={(e) => {
                  setTypeCustom(e.target.value);
                  revalidate({ typeCustom: e.target.value });
                }}
                placeholder={t('connectMeetCreate.typeCustomPh')}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] ${
                  errors.type_custom ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              {errors.type_custom && (
                <p className="mt-1 text-sm text-red-600">{errors.type_custom}</p>
              )}
            </div>
          )}
        </Card>

        {/* ── Foto / galerij ────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-1">{t('connectMeet.photoOptional')}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {t('connectMeet.photoDesc')}
          </p>
          <GalleryPicker
            eventType={selectedType}
            value={imageUrl}
            onChange={setImageUrl}
          />
        </Card>

        {/* ── Datum en tijd ─────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-1">{t('connectMeet.dateAndTime')}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {t('connectMeet.dateAndTimeDesc')}
          </p>

          <div className="space-y-3">
            {dateTimes.map((dt, i) => (
              <DateTimeRow
                key={i}
                index={i}
                value={dt}
                today={today}
                onChange={(v) => updateDateTime(i, v)}
                onRemove={() => removeDateTime(i)}
                isFirst={i === 0}
              />
            ))}
          </div>

          {(errors.event_date || errors.time_from) && (
            <div className="mt-2 space-y-1">
              {errors.event_date && <p className="text-sm text-red-600">{errors.event_date}</p>}
              {errors.time_from && <p className="text-sm text-red-600">{errors.time_from}</p>}
            </div>
          )}

          <button
            type="button"
            onClick={addDateTime}
            className="mt-3 flex items-center gap-1.5 text-sm text-[#0B1F3A] hover:underline"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('connectMeet.addTimeSlot')}
          </button>
        </Card>

        {/* ── Locatie ───────────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-4">{t('connectMeet.location')}</h2>
          <div className="space-y-4">
            {/* Venue / location name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('connectMeet.location')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => {
                    setLocationName(e.target.value);
                    revalidate({ location: e.target.value });
                  }}
                  placeholder={t('connectMeet.locationPlaceholder')}
                  className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] ${
                    errors.location ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location}</p>
              )}
            </div>

            {/* Address + postal code */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('connectMeet.address')}{' '}
                  <span className="text-gray-400 font-normal">({t('connectMeet.optional')})</span>
                </label>
                <input
                  type="text"
                  value={locationAddress}
                  onChange={(e) => setLocationAddress(e.target.value)}
                  placeholder={t('connectMeet.addressPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('connectMeet.postalCode')}{' '}
                  <span className="text-gray-400 font-normal">({t('connectMeet.optional')})</span>
                </label>
                <input
                  type="text"
                  value={locationPostalCode}
                  onChange={(e) => setLocationPostalCode(e.target.value)}
                  placeholder={t('connectMeet.postalCodePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
              </div>
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('connectMeet.city')}{' '}
                <span className="text-gray-400 font-normal">({t('connectMeet.optional')})</span>
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t('connectMeet.cityInputPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              />
            </div>

            {/* Regions – multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('connectMeet.regions')} <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">— {t('connectMeet.selectOneOrMore')}</span>
              </label>
              <MultiCheckbox
                options={provinceOptions}
                value={selectedRegios}
                onChange={(next) => {
                  setSelectedRegios(next);
                  revalidate({ regios: next });
                }}
                columns={3}
              />
              {errors.regios && (
                <p className="mt-2 text-sm text-red-600">{errors.regios}</p>
              )}
            </div>
          </div>
        </Card>

        {/* ── Doelgroep ─────────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-1">Voor wie is dit event?</h2>
          <p className="text-sm text-gray-500 mb-4">
            Selecteer één of meerdere doelgroepen{' '}
            <span className="text-gray-400">(optioneel — leeg = voor iedereen)</span>
          </p>
          <MultiCheckbox
            options={AUDIENCE_OPTIONS}
            value={selectedAudience as any}
            onChange={setSelectedAudience as any}
            columns={2}
          />
        </Card>

        {/* ── Extra details ─────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-semibold text-gray-900 mb-4">Extra details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max. deelnemers{' '}
                <span className="text-gray-400 font-normal">(optioneel)</span>
              </label>
              <input
                type="number"
                value={maxPersons}
                onChange={(e) => setMaxPersons(e.target.value)}
                min="1"
                placeholder={t('connectMeetCreate.noMaximum')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Omschrijving{' '}
                <span className="text-gray-400 font-normal">(optioneel)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder={t('connectMeetCreate.descriptionPh')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
              />
            </div>
          </div>
        </Card>

        {/* ── Submit ────────────────────────────────────────────────────── */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
          <Link
            to="/connect-meet"
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuleren
          </Link>
          <Button
            type="submit"
            loading={createMutation.isPending || opslaanBezig}
            disabled={createMutation.isPending || opslaanBezig}
          >
            {isEdit ? t('connectMeet.saveChanges', 'Wijzigingen opslaan') : 'Event aanmaken'}
          </Button>
        </div>
      </form>
    </div>
  );
};
